"use client";

import React, { useRef, useState, useEffect } from "react";
import { FaMagnifyingGlass } from "react-icons/fa6";
import { useRouter, useSearchParams } from "next/navigation";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { badWords } from "/utils/badWords";
import { Button } from "@mui/material";

export const containsBadWords = (input: string) => {
  const inputLower = input.toLowerCase();
  return badWords.some((badWord) => inputLower.includes(badWord));
};

export interface iProps {
  isMobile: boolean;
}

const SearchHeader = ({ isMobile }: iProps) => {
  const searchParams = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);
  const refSearchField = useRef<HTMLDivElement>(null);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [searchIsOpen, setIsOpen] = useState(false);
  const [keyword, setKeyword] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const router = useRouter();

  const handleClick = () => {
    setIsOpen(!searchIsOpen);
    inputRef.current?.focus();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
    if (error) setError(null); // Clear error when user starts typing
  };

  // Helper to get a reCAPTCHA v3 token for the "search_action"
  const getRecaptchaToken = async (): Promise<string | null> => {
    if (!executeRecaptcha) {
      console.error("reCAPTCHA not yet available");
      return null;
    }
    try {
      const token = await executeRecaptcha("search_action");
      return token;
    } catch (err) {
      console.error("Error obtaining reCAPTCHA token:", err);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isVerifying) return;

    if (!keyword.trim()) {
      setError("Search term cannot be empty.");
      return;
    }

    if (containsBadWords(keyword)) {
      setError("Your search contains inappropriate words. Please revise.");
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      // Get reCAPTCHA token
      const token = await getRecaptchaToken();
      if (!token) {
        setError("Verification failed. Please try again.");
        setIsVerifying(false);
        return;
      }

      // Call the secure API route
      const response = await fetch("/api/search", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ keyword, token }),
      });

      const data = await response.json();

      if (response.ok && data.redirectUrl) {
        router.push(data.redirectUrl);
      } else {
        setError(data.error || "Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("Error during search:", err);
      setError("Something went wrong. Please try again.");
    }

    setIsVerifying(false);
  };

  useEffect(() => {
    if (searchIsOpen) {
      inputRef.current?.focus();
    }
  }, [searchIsOpen]);

  useEffect(() => {
    if (searchIsOpen) {
      const handleOutSideClick = (event: Event) => {
        if (!refSearchField.current?.contains(event.target as Node)) {
          setIsOpen(false);
          setKeyword("");
        }
      };
      window.addEventListener("mousedown", handleOutSideClick);
      return () => {
        window.removeEventListener("mousedown", handleOutSideClick);
      };
    }
  }, [searchIsOpen]);

  // 🔹 Mobile version
  if (isMobile) {
    return (
      <>
        <button
          title="Open Search"
          aria-label="Open Search"
          onClick={handleClick}
          className={`absolute text-black right-[48px] top-[-36px] bg-transparent pt-[0px] ${
            searchIsOpen ? `mt-[8px]` : `mt-[8px]`
          } flex flex-col justify-center items-center align-middle h-4 w-4 ${
            searchIsOpen ? `lg:pt-0` : ``
          }`}
          disabled={isVerifying}
        >
          <FaMagnifyingGlass />
        </button>

        <div
          className={`bg-white lg rounded-md shadow-sm left-0 ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset px-2 ${
            searchIsOpen
              ? `w-full transition-all mx-auto mt-[0px] lg:mt-0 lg:max-w-[240px] lg:-left-[250px]`
              : `hidden w-0 transition-all`
          }`}
          ref={refSearchField}
        >
          <form onSubmit={handleSubmit} className="w-full flex">
            <input
              type="text"
              title="Search"
              aria-label="Search"
              id="search"
              autoComplete="search"
              className="block height-[40px] flex-1 w-full border-0 focus-visible:ring-0 focus-visible:outline-none bg-transparent p-3 text-gray-900 placeholder:text-gray-400 focus:ring-0 h-[32px]"
              placeholder={error || "Search..."}
              onChange={handleChange}
              ref={inputRef}
              disabled={isVerifying}
            />
          </form>
        </div>
      </>
    );
  }

  // 🔹 Desktop version
  return (
    <>
      <div
        className={`bg-white absolute lg rounded-md shadow-sm left-[1%] sm:left-auto ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset ${
          searchIsOpen
            ? `w-[98%] transition-all mx-auto mt-[44px] sm:mt-0 sm:-ml-[250px] lg:mt-0 sm:max-w-[240px]`
            : `hidden w-0 transition-all`
        } relative`}
        ref={refSearchField}
      >
        <form onSubmit={handleSubmit} className="w-full flex">
          <input
            type="text"
            id="search"
            autoComplete="search"
            className="block flex-1 w-full border-0 bg-transparent p-3 focus-visible:outline-none text-gray-900 placeholder:text-gray-400 focus:ring-0 h-[32px]"
            placeholder={error || "Search..."}
            onChange={handleChange}
            ref={inputRef}
            disabled={isVerifying}
          />
        </form>
        {error && (
          <p className="text-red-500 text-sm absolute top-full left-0 mt-1 bg-white p-1 rounded shadow-sm">
            {error}
          </p>
        )}
      </div>
      <button
        title="Search"
        aria-label="Search"
        onClick={handleClick}
        className={`bg-transparent pt-[0px] ${
          searchIsOpen ? `mt-[8px] mb-[62px] sm:mb-0 lg:pt-0` : `mt-[8px]`
        } flex flex-col text-black justify-center items-center align-middle ml-1 h-4 w-4`}
        disabled={isVerifying}
      >
        <FaMagnifyingGlass />
      </button>
    </>
  );
};

export default SearchHeader;
